import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Camera, Info } from "lucide-react";
import { captureSnapshot, loadDriftReport, type DriftSignal } from "@/lib/api";

/**
 * Drift monitoring page. Two surfaces:
 * - "Snapshot now" — captures current discovery state as a baseline.
 *   Drift detection compares latest vs prior; without two snapshots
 *   the page renders the "no baseline" notice.
 * - The signals list — categorised by severity per spec §5
 *   Component 6.
 */
export function DriftPage() {
  const tenantId = "_default";
  const qc = useQueryClient();

  const drift = useQuery({
    queryKey: ["semantic-drift", tenantId],
    queryFn: () => loadDriftReport(tenantId),
  });

  const snapshot = useMutation({
    mutationFn: () => captureSnapshot(tenantId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["semantic-drift", tenantId] }),
  });

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <header className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold text-semantic-ink">
            Drift monitoring
          </h1>
          <p className="mt-0.5 text-sm text-semantic-slate">
            New conflicts on watched metrics, out-of-band changes to
            trusted definitions, ownership churn, dbt-only drift.
            Compares the latest snapshot to the prior baseline.
          </p>
        </div>
        <button
          type="button"
          data-tour-id="drift-snapshot"
          onClick={() => snapshot.mutate()}
          disabled={snapshot.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-semantic-primary px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-semantic-primaryDark disabled:opacity-60"
        >
          <Camera size={12} className={snapshot.isPending ? "animate-spin" : undefined} />
          Snapshot now
        </button>
      </header>

      {drift.data && (
        <div className="mb-4 grid grid-cols-3 gap-3">
          <SeverityCard label="HIGH" tone="danger" count={drift.data.highCount} />
          <SeverityCard label="MEDIUM" tone="warn" count={drift.data.mediumCount} />
          <SeverityCard label="LOW" tone="slate" count={drift.data.lowCount} />
        </div>
      )}

      {drift.data?.notice && <Notice message={drift.data.notice} />}

      {drift.isLoading && <Skeleton />}
      {drift.error && (
        <ErrorBanner message={(drift.error as Error).message} />
      )}
      {drift.data && drift.data.signals.length > 0 && (
        <ul data-tour-id="drift-signals" className="space-y-2">
          {drift.data.signals.map((s, i) => (
            <li key={i}>
              <SignalRow signal={s} />
            </li>
          ))}
        </ul>
      )}
      {drift.data &&
        drift.data.signals.length === 0 &&
        !drift.data.notice && <CleanState />}
    </div>
  );
}

function SeverityCard({
  label,
  tone,
  count,
}: {
  label: string;
  tone: "danger" | "warn" | "slate";
  count: number;
}) {
  const toneClass =
    tone === "danger"
      ? "border-semantic-danger/30 bg-semantic-danger/5 text-semantic-danger"
      : tone === "warn"
        ? "border-semantic-warn/30 bg-semantic-warn/5 text-semantic-warn"
        : "border-semantic-slate/30 bg-semantic-mist text-semantic-slate";
  return (
    <div className={`rounded-xl2 border p-4 ${toneClass}`}>
      <div className="text-2xs font-medium uppercase tracking-wide">{label}</div>
      <div className="font-mono text-3xl">{count}</div>
    </div>
  );
}

function SignalRow({ signal }: { signal: DriftSignal }) {
  return (
    <div className="rounded-xl2 border border-semantic-slate/20 bg-white p-3 shadow-card">
      <div className="flex items-center gap-2 text-sm">
        <SeverityBadge severity={signal.severity} />
        <span className="text-semantic-ink">{signal.summary}</span>
      </div>
      <div className="mt-1 text-2xs text-semantic-slate">
        Detected {new Date(signal.detectedAt).toLocaleString()}
      </div>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: DriftSignal["severity"] }) {
  const tone =
    severity === "HIGH"
      ? "bg-semantic-danger/15 text-semantic-danger"
      : severity === "MEDIUM"
        ? "bg-semantic-warn/15 text-semantic-warn"
        : "bg-semantic-slate/15 text-semantic-slate";
  return (
    <span className={`rounded-full px-2 py-0.5 text-2xs font-medium ${tone}`}>
      {severity}
    </span>
  );
}

function Notice({ message }: { message: string }) {
  return (
    <div className="mb-4 flex items-center gap-2 rounded-xl2 border border-semantic-slate/30 bg-semantic-mist p-3 text-sm text-semantic-ink">
      <Info size={14} aria-hidden className="text-semantic-primary" />
      <span>{message}</span>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-14 animate-pulse rounded-xl2 bg-semantic-mist/60" />
      ))}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-xl2 border border-semantic-danger/30 bg-semantic-danger/5 p-4 text-sm text-semantic-danger">
      Couldn't load drift report — {message}
    </div>
  );
}

function CleanState() {
  return (
    <div className="rounded-xl2 border border-dashed border-semantic-slate/30 bg-white p-10 text-center">
      <AlertTriangle size={28} aria-hidden className="mx-auto text-semantic-success/60" />
      <p className="mt-2 text-sm font-medium text-semantic-ink">No drift detected.</p>
      <p className="mt-1 text-2xs text-semantic-slate">
        The latest snapshot matches the prior baseline. No new conflicts,
        out-of-band trust modifications, ownership changes, or dbt drift.
      </p>
    </div>
  );
}
